'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ForwardedRef,
} from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { ICON_SIZE, Avatar, Button, Card } from '@mokoboko/components';
import { useElementHeight } from '@/hooks/use-element-height';
import { mergeClassNames } from '@/lib/utils';
import { SubstituteBadge } from '@/components/substitute-badge';
import { EmptyCalendarState } from '@/components/empty-calendar-state';
import { useCalendarContext, useBookingContext } from './calendar-context';
import {
  toDateKey,
  isMonthEmpty,
  visibleSpots,
  getClosestAvailableDate,
  formatSelectedDate,
  formatBookingSummaryDate,
  isSlotFull,
  groupSlotsByInstructor,
} from './calendar-utils';
import type {
  CalendarTimeSlotsProps,
  CalendarBookActionProps,
  CalendarEmptyMonthProps,
} from './calendar.types';

function useRequiredBookingContext() {
  const booking = useBookingContext();
  if (!booking)
    throw new Error(
      'Calendar.TimeSlots / Calendar.BookAction / Calendar.EmptyMonth require booking mode (pass `availability` to Calendar.Root)'
    );
  return booking;
}

function assignRef<T>(ref: ForwardedRef<T>, node: T | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref) ref.current = node;
}

function formatInstructorShort(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}`;
}

const TimeSlots = forwardRef<HTMLDivElement, CalendarTimeSlotsProps>(
  ({ className, emptyLabel = 'Select a date', emptyMonthTitle, emptyMonthDescription, ...props }, ref) => {
    const ctx = useCalendarContext();
    const booking = useRequiredBookingContext();

    const selectedDate = ctx.selected instanceof Date ? ctx.selected : null;
    const dateKey = selectedDate ? toDateKey(selectedDate) : null;
    const slots = dateKey ? (booking.availability[dateKey] ?? []) : [];

    const groups = useMemo(
      () => groupSlotsByInstructor(slots, booking.showInstructor),
      [slots, booking.showInstructor]
    );
    // A sole instructor repeats the page header on every date; only a
    // substitute day says something new.
    const hideInstructorRow =
      booking.soleInstructor &&
      groups.length === 1 &&
      !groups[0].isSubstitute;

    const selectedRef = useRef<HTMLButtonElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      const el = selectedRef.current;
      const container = scrollRef.current;
      if (el && container && container.scrollHeight > container.clientHeight) {
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offsetInContainer = elRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({
          top: offsetInContainer - container.clientHeight / 2 + el.offsetHeight / 2,
          behavior: 'smooth',
        });
      }
    }, [booking.selectedSlot?.id]);

    if (!selectedDate) {
      const monthEmpty = isMonthEmpty(booking.availability, ctx.currentMonth);

      return (
        <div
          ref={ref}
          className={mergeClassNames('flex-1 basis-full md:basis-[260px] min-w-0 md:min-w-[260px]', className)}
          {...props}
        >
          {monthEmpty ? (
            <EmptyCalendarState
              className="md:min-h-[24rem] md:justify-center"
              title={emptyMonthTitle ?? 'No slots to show'}
              description={emptyMonthDescription ?? 'Use the calendar to find a month with available slots.'}
            />
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              {emptyLabel}
            </p>
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={mergeClassNames('flex-1 basis-full md:basis-[260px] min-w-0 md:min-w-[260px]', className)}
        {...props}
      >
        <div className="flex items-center justify-center pt-2.5 md:pt-5 lg:pt-5 pb-3 md:pb-4">
          <h3 className="heading-sub">
            {formatSelectedDate(selectedDate, ctx.locale)}
          </h3>
        </div>

        <div ref={scrollRef} className="md:overflow-y-auto md:max-h-[28rem] mb-scrollbar-thin">
        <div className="flex flex-col gap-6 pt-1 pb-[72px] md:py-3">
          {groups.map((group) => (
            <div key={group.instructorId}>
              {group.instructorName && !hideInstructorRow && (
                <div className="flex items-center gap-3 mb-3">
                  <Avatar name={group.instructorName} src={group.instructorAvatar} size="xs" />
                  <span className="text-sm font-medium leading-5 text-gray-900 shrink-0 capitalize">
                    {formatInstructorShort(group.instructorName)}
                  </span>
                  {group.isSubstitute && <SubstituteBadge />}
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                {group.slots.map((slot) => {
                  const isSel = booking.selectedSlot?.id === slot.id;
                  const isFullyBooked = isSlotFull(slot);
                  const isBooked = isFullyBooked && booking.isSlotBooked(slot);
                  const waitlistJoinable = isFullyBooked && !isBooked && booking.isSlotWaitlistJoinable(slot);
                  const spots = visibleSpots(slot, booking);
                  const displayTime = booking.formatSlotTime(slot.startTime, slot.timezone);

                  return (
                    <button
                      key={slot.id}
                      ref={isSel ? selectedRef : undefined}
                      type="button"
                      disabled={isFullyBooked && !isBooked && !waitlistJoinable}
                      aria-pressed={isSel}
                      onClick={() => booking.selectSlot(slot)}
                      className={mergeClassNames(
                        'mb-focus-visible relative w-full rounded-xl px-3 py-5 md:py-3 min-h-[62px] text-center flex flex-col items-center justify-center transition-[background-color,color,box-shadow,transform,opacity] duration-200 ease-out enabled:active:scale-[0.97] enabled:active:duration-75 outline-none',
                        isSel
                          ? 'bg-ink text-white shadow-[inset_0_0_0_1px_theme(colors.ink.DEFAULT)]'
                          : 'bg-white text-gray-700 shadow-skeu-secondary hover:bg-gray-200 cursor-pointer',
                        isFullyBooked && !isBooked && !waitlistJoinable && 'bg-gray-100 !text-gray-600 !shadow-[inset_0_0_0_1px_theme(colors.gray.200)] hover:bg-gray-100 cursor-not-allowed',
                        (waitlistJoinable || isBooked) && !isSel && 'bg-gray-100 text-gray-600 shadow-[inset_0_0_0_1px_theme(colors.gray.200)] hover:bg-gray-200 cursor-pointer',
                      )}
                    >
                      {isSel && (
                        <span className="absolute top-2 right-2 animate-btn-spinner-in">
                          <Check size={ICON_SIZE.sm} className="text-white" strokeWidth={2.5} />
                        </span>
                      )}
                      <span className="block text-base md:text-sm font-semibold leading-6 md:leading-5 text-inherit">
                        {displayTime}
                      </span>
                      {isBooked ? (
                        <span className={mergeClassNames('block text-xs leading-4 mt-0.5', isSel ? 'text-green-300' : 'text-green-600')}>
                          Booked
                        </span>
                      ) : waitlistJoinable ? (
                        <span className={mergeClassNames('block text-xs leading-4 mt-0.5', isSel ? 'text-yellow-300' : 'text-yellow-600')}>
                          Waitlist
                        </span>
                      ) : isFullyBooked && booking.showFullyBooked ? (
                        <span className="block text-xs leading-4 mt-0.5 text-gray-500">
                          Fully booked
                        </span>
                      ) : !isFullyBooked && spots ? (
                        <span className={mergeClassNames('block text-xs leading-4 mt-0.5', isSel ? 'text-gray-300' : spots.className)}>
                          {spots.text}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {slots.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No available slots
            </p>
          )}
        </div>
        </div>
      </div>
    );
  }
);
TimeSlots.displayName = 'Calendar.TimeSlots';

// On desktop the bar floats over whatever card it lands on, so a soft blur
// band spans the window under it. The band follows the bar's height, ends a
// little above its top edge and sits beneath it, so the bar's shadow stays
// on top of the blur. One blur faded by a mask leaves a hard line where it
// reaches full strength; stacked layers, each stronger and each fading out
// lower, build it up evenly from the top of the band to the bottom.
const BAR_OFFSET_PX = 32;
const BLUR_OVERHANG_PX = 24;
const BLUR_LAYERS = [
  { blur: 2, solidTo: 60, fadeTo: 100 },
  { blur: 4, solidTo: 40, fadeTo: 80 },
  { blur: 8, solidTo: 20, fadeTo: 60 },
].map(({ blur, solidTo, fadeTo }) => {
  const mask = `linear-gradient(to top, black ${solidTo}%, transparent ${fadeTo}%)`;
  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    maskImage: mask,
    WebkitMaskImage: mask,
  };
});

const BookAction = forwardRef<HTMLDivElement, CalendarBookActionProps>(
  ({ label, classTitle, price, variant = 'primary', className, ...props }, ref) => {
    const ctx = useCalendarContext();
    const booking = useRequiredBookingContext();

    const selectedDate = ctx.selected instanceof Date ? ctx.selected : null;
    const slot = booking.selectedSlot;
    const isReady = !!selectedDate && !!slot;

    const [measureBar, barHeight] = useElementHeight();
    const setBarRef = useCallback(
      (node: HTMLDivElement | null) => {
        measureBar(node);
        assignRef(ref, node);
      },
      [measureBar, ref]
    );

    if (!isReady) return null;

    const summaryDate = formatBookingSummaryDate(selectedDate, ctx.locale);
    const displayTime = booking.formatSlotTime(slot.startTime, slot.timezone);

    const shortInstructor =
      booking.showInstructor && slot.instructorName
        ? formatInstructorShort(slot.instructorName)
        : null;

    return (
      <>
        <div
          aria-hidden
          className="pointer-events-none fixed inset-x-0 bottom-0 z-dropdown hidden md:block"
          style={{ height: barHeight + BAR_OFFSET_PX + BLUR_OVERHANG_PX }}
        >
          {BLUR_LAYERS.map((style) => (
            <div
              key={style.backdropFilter}
              className="absolute inset-0 animate-blur-vignette-show"
              style={style}
            />
          ))}
        </div>
        <div
          ref={setBarRef}
          className={mergeClassNames(
            'fixed bottom-0 left-0 right-0 z-dropdown bg-white px-5 pt-6 pb-[max(2rem,env(safe-area-inset-bottom))] animate-book-action-show shadow-[0_-12px_16px_-4px_rgba(10,13,18,0.08),0_-4px_6px_-2px_rgba(10,13,18,0.03),0_-2px_2px_-1px_rgba(10,13,18,0.04)]',
            'md:bottom-8 md:left-1/2 md:w-full md:max-w-2xl md:px-5 md:py-4 md:rounded-xl md:border md:border-gray-200 md:shadow-lg md:bg-white md:animate-book-action-show-centered',
            className,
          )}
          {...props}
        >
          <div className="md:hidden">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="min-w-0 space-y-1.5">
                <p className="text-base font-semibold leading-6 text-gray-900 truncate">
                  {classTitle}
                </p>
                <div className="flex items-center gap-1.5 text-sm leading-5 text-gray-700 flex-wrap">
                  <span>{summaryDate}</span>
                  <span className="text-gray-400">&middot;</span>
                  <span>{displayTime}</span>
                  {shortInstructor && (
                    <>
                      <span className="text-gray-400">&middot;</span>
                      <span className="capitalize">{shortInstructor}</span>
                      {slot.isSubstitute && <SubstituteBadge />}
                    </>
                  )}
                </div>
              </div>
              {price && <div className="shrink-0">{price}</div>}
            </div>
            <Button
              variant={variant}
              size="xl"
              onClick={booking.handleBook}
              disabled={booking.isProcessing}
              isProcessing={booking.isProcessing}
              className="w-full"
            >
              {label ?? booking.bookLabel}
              <ChevronRight size={ICON_SIZE.sm} strokeWidth={2} />
            </Button>
          </div>

          <div className="hidden md:flex md:flex-wrap md:items-center md:gap-4">
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-base font-semibold leading-6 text-gray-900 truncate">
                {classTitle}
              </p>
              <div className="flex items-center gap-1.5 text-sm leading-5 text-gray-700 flex-wrap">
                <span>{summaryDate}</span>
                <span className="text-gray-400">&middot;</span>
                <span>{displayTime}</span>
                {shortInstructor && (
                  <>
                    <span className="text-gray-400">&middot;</span>
                    <span className="capitalize">{shortInstructor}</span>
                    {slot.isSubstitute && <SubstituteBadge />}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {price && <div className="shrink-0">{price}</div>}
              <Button
                variant={variant}
                size="lg"
                onClick={booking.handleBook}
                disabled={booking.isProcessing}
                isProcessing={booking.isProcessing}
              >
                {label ?? booking.bookLabel}
                <ChevronRight size={ICON_SIZE.sm} strokeWidth={2} />
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }
);
BookAction.displayName = 'Calendar.BookAction';

const EmptyMonth = forwardRef<HTMLDivElement, CalendarEmptyMonthProps>(
  (
    {
      heading = 'No classes this month',
      description = 'Jump to the nearest month with available classes.',
      ctaLabel = 'Go to available month',
      noAvailability = false,
      noAvailabilityHeading = 'No classes available',
      noAvailabilityDescription = 'There are no classes scheduled in the coming weeks.',
      className,
      ...props
    },
    ref
  ) => {
    const ctx = useCalendarContext();
    const booking = useRequiredBookingContext();

    const monthEmpty = isMonthEmpty(booking.availability, ctx.currentMonth);
    const closestDate = useMemo(
      () =>
        monthEmpty
          ? getClosestAvailableDate(booking.availability, ctx.currentMonth)
          : null,
      [monthEmpty, booking.availability, ctx.currentMonth]
    );

    const handleGoToAvailable = useCallback(() => {
      if (closestDate) {
        ctx.setCurrentMonth(
          new Date(closestDate.getFullYear(), closestDate.getMonth(), 1)
        );
        ctx.selectDate(closestDate);
      } else {
        const floor = ctx.minDate ?? new Date();
        ctx.setCurrentMonth(
          new Date(floor.getFullYear(), floor.getMonth(), 1)
        );
      }
    }, [closestDate, ctx]);

    if (!monthEmpty) return null;

    const showTerminalState = noAvailability && !closestDate;

    return (
      <div
        ref={ref}
        className={mergeClassNames(
          'absolute inset-0 top-24 flex items-center justify-center -mt-4',
          className
        )}
        {...props}
      >
        <Card className="shadow-lg max-w-[280px] text-center">
          <p className="text-base font-semibold leading-6 text-gray-900">
            {showTerminalState ? noAvailabilityHeading : heading}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            {showTerminalState ? noAvailabilityDescription : description}
          </p>
          {!showTerminalState && (
            <Button
              variant="primary"
              size="md"
              className="mt-4 w-full"
              onClick={handleGoToAvailable}
            >
              {ctaLabel}
            </Button>
          )}
        </Card>
      </div>
    );
  }
);
EmptyMonth.displayName = 'Calendar.EmptyMonth';

export { TimeSlots, BookAction, EmptyMonth };
